import { Host, h } from "@stencil/core";
import { KEY_CODES } from "../../../common/reserverd-names";
const NAME = "name";
const PART_PREFIX = "dm-item__";
const CANCEL_CLASS = "button-cancel";
const CONFIRM_CLASS = "button-confirm";
const BUTTON_CONFIRM_PART = (disabledPart) => `${PART_PREFIX}button-action confirm${disabledPart}`;
const BUTTON_CANCEL_PART = (disabledPart) => `${PART_PREFIX}button-action cancel${disabledPart}`;
const SELECT_OPTION_PART = `${PART_PREFIX}select-option`;
/**
 * @slot items - The first level items (entities) of the data model
 */
export class NextDataModelingSubitem {
    constructor() {
        this.expanded = false;
        this.errorType = "None";
        this.waitingMode = "none";
        this.actionsVisible = true;
        this.captions = undefined;
        this.dataType = "";
        this.disabled = false;
        this.entityNameToATTs = {};
        this.errorTexts = undefined;
        this.fieldNames = [];
        this.level = 1;
        this.maxAtts = 3;
        this.mode = "normal";
        this.name = "";
        this.readonly = false;
        this.showNewFieldBtn = true;
        this.type = "LEVEL";
    }
    /**
     * The metadata used for the different actions in the UI.
     */
    actions = (captions, disabledPart) => ({
        add: [
            {
                // Confirm add
                label: captions.confirm,
                class: CONFIRM_CLASS,
                part: BUTTON_CONFIRM_PART(disabledPart),
                event: this.confirmAction("new")
            },
            {
                // Cancel add
                label: captions.cancel,
                class: CANCEL_CLASS,
                part: BUTTON_CANCEL_PART(disabledPart),
                event: this.toggleShowNewField
            }
        ],
        delete: [
            {
                // Confirm delete
                label: captions.confirm,
                class: undefined,
                part: `${PART_PREFIX}button-delete-action confirm${disabledPart}`,
                event: this.emitDelete
            },
            {
                // Cancel delete
                label: captions.cancel,
                class: undefined,
                part: `${PART_PREFIX}button-delete-action cancel${disabledPart}`,
                event: this.toggleMode("delete")
            }
        ],
        edit: [
            {
                // Confirm edit
                label: captions.confirm,
                class: CONFIRM_CLASS,
                part: BUTTON_CONFIRM_PART(disabledPart),
                event: this.confirmAction("edit")
            },
            {
                // Cancel edit
                label: captions.cancel,
                class: CANCEL_CLASS,
                part: BUTTON_CANCEL_PART(disabledPart),
                event: this.toggleMode("edit")
            }
        ],
        normal: [
            {
                // Edit action
                label: captions.edit,
                class: "button-primary button-edit",
                part: `${PART_PREFIX}button-primary edit${disabledPart}`,
                event: this.toggleMode("edit")
            },
            {
                // Delete action
                label: captions.delete,
                class: "button-primary button-delete",
                part: `${PART_PREFIX}button-primary delete${disabledPart}`,
                event: this.toggleMode("delete")
            }
        ]
    });
    errorName;
    lastEditInfo = { name: "", type: "ATT" };
    focusInputInNextRender = false;
    // Refs
    inputName;
    inputType;
    el;
    /**
     * Fired when the item is confirmed to be deleted
     */
    deleteField;
    /**
     * Fired when the item is edited
     */
    editField;
    /**
     * Fired when a new entity is committed to be added
     */
    newEntity;
    /**
     * Fired when a new file is committed to be added when adding a new entity (level === 0)
     */
    firstNewField;
    /**
     * Fired when the new field of the new entity tries to commits the adding
     * operation, but fails because it has errors
     */
    firstNewFieldErrors;
    /**
     * Fired when a new file is committed to be added
     */
    newField;
    handleExpandedChange(event) {
        event.stopPropagation();
    }
    handleFirstNewField(event) {
        if (this.level !== 0) {
            return;
        }
        this.confirmAction("new", "no", event.detail)(event);
    }
    handleFirstNewFieldErrors(event) {
        this.confirmAction("new", "yes")(event);
    }
    /**
     * Set the adding mode for the first field of the entity.
     */
    async setAddingMode() {
        this.clearInput();
        this.waitingMode = "adding";
    }
    /**
     * Remove the value of the input when mode === "add" | "edit"
     */
    async clearInput() {
        if (this.inputName) {
            const inputRef = this.inputName.shadowRoot
                .firstElementChild; // TODO: Improve typing
            inputRef.value = "";
        }
        this.errorType = "None";
    }
    /**
     * Check errors in the item when `level !== 0`
     */
    async checkErrors(errors, event) {
        this.confirmAction("new", errors)(event);
    }
    /**
     * Deletes the field.
     */
    async delete(event) {
        this.emitDelete(event);
    }
    /**
     * Hides the waiting mode to continue editing the field.
     */
    async hideWaitingMode() {
        if (this.mode === "add" && this.level === 0) {
            const entityItem = this.el.firstElementChild
                .firstElementChild;
            entityItem.hideWaitingMode();
        }
        // The add mode should not be changed
        else if (this.mode !== "add") {
            this.mode = "normal";
        }
        this.waitingMode = "none";
    }
    /**
     * Returns:
     *   @example ```(Scorer, Goals)```
     *   @example ```(Name, Age, Nationality (+3))```
     */
    makeAttsPrettier = (atts, maxAtts) => atts.length <= maxAtts
        ? "(" + atts.join(", ") + ")"
        : `(${atts.slice(0, maxAtts).join(", ")} (+${atts.length - maxAtts}))`;
    emitDelete = (event) => {
        // The subitem is the last one of the parent. Delete the parent instead
        if (this.level !== 0 && this.fieldNames.length === 1) {
            const parentItem = this.el.parentElement
                .parentElement;
            parentItem.delete(event);
            this.mode = "normal";
            return;
        }
        event.stopPropagation();
        this.waitingMode = "deleting";
        this.deleteField.emit();
    };
    toggleMode = (mode) => (event) => {
        event.stopPropagation();
        this.mode = this.mode === "normal" ? mode : "normal";
        this.focusInputInNextRender = mode === "edit";
    };
    toggleShowNewField = (event) => {
        event.stopPropagation();
        this.errorType = "None";
        const showNewFieldBtn = !this.showNewFieldBtn;
        this.showNewFieldBtn = showNewFieldBtn;
        this.focusInputInNextRender = !showNewFieldBtn;
        if (!showNewFieldBtn && this.level === 0) {
            const entityItem = this.el.firstElementChild
                .firstElementChild;
            entityItem.clearInput();
        }
    };
    /**
     * @todo TODO: Improve typing
     * WA function to get the gx-edit's input value
     * @param editElement An HTMLGxEditElement that is implemented in the web-controls-library
     */
    getGxEditInputValue = (editElement) => editElement.shadowRoot.firstElementChild.value;
    handleKeyDown = (actionType) => (event) => {
        if (event.code !== KEY_CODES.ENTER) {
            return;
        }
        event.preventDefault();
        this.confirmAction(actionType)(event);
    };
    checkChildErrors = (errors, event) => {
        this.el.firstElementChild
            .firstElementChild.checkErrors(errors, event);
    };
    validateErrorsWhenAddingEntity = (actionType, errors = "unknown", event) => {
        // Validate child errors when adding a new entity
        if (this.level === 0 && actionType === "new" && errors === "unknown") {
            this.checkChildErrors(errors, event);
        }
        // Validate parent errors when adding a new field in the new entity
        else if (!this.actionsVisible && errors === "unknown") {
            this.firstNewFieldErrors.emit();
        }
    };
    confirmAction = (actionType, errors = "unknown", itemInfo = null) => (event) => {
        event.stopPropagation();
        const trimmedInput = this.getGxEditInputValue(this.inputName).trim();
        // Force re-render. Useful when the error type don't change but the
        // displayed error text must change
        this.errorType = "None";
        if (trimmedInput === "") {
            this.errorType = "Empty";
            this.validateErrorsWhenAddingEntity(actionType, errors, event);
            return;
        }
        // The field already exists
        if (this.name !== trimmedInput &&
            this.fieldNames.includes(trimmedInput)) {
            this.errorType = "AlreadyDefined";
            this.errorName = trimmedInput;
            this.validateErrorsWhenAddingEntity(actionType, errors, event);
            return;
        }
        // New field
        if (actionType === "new") {
            this.lastEditInfo = {
                name: trimmedInput,
                type: this.level === 0 ? "ATT" : this.inputType.value // Doesn't matter the type when level = 0
            };
            // Adding a entity
            if (this.level === 0) {
                // Must check child errors
                if (errors === "unknown") {
                    this.checkChildErrors(errors, event);
                }
                // The child does not have errors, commit the new entity
                else if (errors === "no") {
                    this.waitingMode = "adding";
                    this.expanded = true;
                    this.newEntity.emit({ name: trimmedInput, level: [itemInfo] });
                    this.toggleShowNewField(event);
                    this.el.firstElementChild
                        .firstElementChild.setAddingMode(); // TODO: Improve typing
                }
            }
            // Normal case: Adding a field that is not the first field of the entity
            else if (this.actionsVisible) {
                this.waitingMode = "adding";
                this.newField.emit(this.lastEditInfo);
                this.toggleShowNewField(event);
            }
            // Adding a field that is the first field of the entity
            else {
                this.firstNewField.emit(this.lastEditInfo);
            }
            return;
        }
        // Edit field (level 0 fields don't have a type, because they are always entities)
        if (this.name !== trimmedInput ||
            (this.level !== 0 && this.type !== this.inputType.value)) {
            this.waitingMode = "editing";
            this.lastEditInfo = {
                name: trimmedInput,
                type: this.level === 0 ? "ATT" : this.inputType.value // Doesn't matter the type when level = 0
            };
            this.editField.emit(this.lastEditInfo);
        }
        this.toggleMode("edit")(event);
    };
    loading = () => (h("svg", { class: "waiting-mode__loading", height: "28", viewBox: "6 6 12 12" }, h("circle", { cx: "12", cy: "12", r: "4", "stroke-width": "1.125" })));
    errorText = (errorTexts) => (h("p", { class: "error-text", part: `${PART_PREFIX}error-text` }, this.errorType === "Empty"
        ? errorTexts.Empty
        : [
            this.level === 0
                ? errorTexts.AlreadyDefinedEntity1
                : errorTexts.AlreadyDefined1,
            h("span", { part: `${PART_PREFIX}error-text-name` }, this.errorName),
            errorTexts.AlreadyDefined2
        ]));
    readonlyContent = (captions, name, type) => [
        // Readonly
        h("h1", { id: NAME, class: {
                name: true,
                "name-entity": type === "ENTITY"
            }, part: `${PART_PREFIX}name` }, name),
        this.level !== 0 && type !== "ATT" && (h("span", { class: "type", part: type === "LEVEL" ? `${PART_PREFIX}collection` : `${PART_PREFIX}entity` }, type === "LEVEL"
            ? captions.collection
            : this.makeAttsPrettier(this.entityNameToATTs[this.dataType] || [], this.maxAtts)))
    ];
    editableContent = (actionType, captions, disabledPart, errorPart) => [
        // Editable
        h("gx-edit", { class: "field-name", part: `${PART_PREFIX}input${errorPart}${disabledPart}`, disabled: this.disabled, type: "text", value: this.name, ref: el => (this.inputName = el), onKeydown: this.handleKeyDown(actionType) }),
        this.level !== 0 && (h("div", { class: "select-wrapper" }, h("select", { class: "select", part: `${PART_PREFIX}input${disabledPart} select`, disabled: this.disabled, ref: el => (this.inputType = el) }, h("option", { part: SELECT_OPTION_PART, value: "ATT", selected: this.type === "ATT" }, captions.ATT), h("option", { part: SELECT_OPTION_PART, value: "ENTITY", selected: this.type === "ENTITY" }, captions.ENTITY), h("option", { part: SELECT_OPTION_PART, value: "LEVEL", selected: this.type === "LEVEL" }, captions.LEVEL)))),
        this.errorType !== "None" && this.errorText(this.errorTexts)
    ];
    newFieldMode = (captions, errorPart, disabledPart, actions) => this.showNewFieldBtn ? (h("button", { class: "button-new-entity", part: `${PART_PREFIX}button-new-entity${disabledPart}`, disabled: this.disabled, type: "button", onClick: this.toggleShowNewField }, this.level === 0 ? captions.addNewEntity : captions.addNewField)) : (h("div", { slot: this.level === 0 ? "header" : undefined, class: {
            [`add-new-field--level-${this.level}`]: true,
            "add-new-field--no-actions": !this.actionsVisible
        }, part: `${PART_PREFIX}header-content`, tabindex: this.level !== 0 && this.actionsVisible ? "0" : undefined }, this.level === 2 && (h("div", { "aria-hidden": "true", class: "sub-field", part: `${PART_PREFIX}sub-field` })), h("h1", { class: "name", part: `${PART_PREFIX}name` }, this.level === 0 ? captions.newEntity : captions.newField), this.editableContent("new", captions, disabledPart, errorPart), this.level === 0 && h("slot", null), this.actionsVisible && [
        h("button", { "aria-label": actions[0].label, class: actions[0].class, part: actions[0].part, disabled: this.disabled, type: "button", onClick: actions[0].event }),
        h("button", { "aria-label": actions[1].label, class: actions[1].class, part: actions[1].part, disabled: this.disabled, type: "button", onClick: actions[1].event })
    ]));
    normalMode = (captions, errorPart, disabledPart, waitingModePart, actions, showWaitingModeTexts) => [
        h("div", { slot: this.level === 0 ? "header" : undefined, class: {
                header: true,
                "edit-mode": this.mode === "edit"
            }, part: `${PART_PREFIX}header-content ${this.level !== 0 ? this.type.toLowerCase() : ""}`, tabindex: this.level !== 0 ? "0" : undefined }, this.level === 2 && (h("div", { "aria-hidden": "true", class: "sub-field", part: `${PART_PREFIX}sub-field` })), this.mode === "edit"
            ? this.editableContent("edit", captions, disabledPart, errorPart)
            : this.readonlyContent(captions, showWaitingModeTexts ? this.lastEditInfo.name : this.name, showWaitingModeTexts ? this.lastEditInfo.type : this.type), !this.readonly && this.actionsVisible && (h("div", { class: {
                "delete-mode": this.mode === "delete",
                optimization: this.mode !== "delete",
                "waiting-mode": this.waitingMode !== "none"
            }, part: `${PART_PREFIX}delete-mode${waitingModePart}` }, this.waitingMode === "none"
            ? [
                this.mode === "delete" && captions.deleteMode,
                h("button", { "aria-label": actions[0].label, class: actions[0].class, part: actions[0].part, disabled: this.disabled, type: "button", onClick: actions[0].event }),
                h("button", { "aria-label": actions[1].label, class: actions[1].class, part: actions[1].part, disabled: this.disabled, type: "button", onClick: actions[1].event })
            ]
            : [captions[this.waitingMode], this.loading()]))),
        this.type === "LEVEL" &&
            (this.level === 0 ? (h("div", { slot: "content", part: `${PART_PREFIX}content` }, h("slot", null))) : (h("slot", null)))
    ];
    componentDidUpdate() {
        // Focus the edit input when the render method has finished
        if (this.focusInputInNextRender && this.inputName) {
            this.focusInputInNextRender = false;
            // Wait until the gx-edit control has render
            requestAnimationFrame(() => {
                this.inputName.click(); // The click method focuses the inner input of the gx-edit
            });
        }
    }
    render() {
        // Parts
        const disabledPart = this.disabled ? " disabled" : "";
        const waitingModePart = this.waitingMode === "none" ? "" : ` ${PART_PREFIX}waiting-mode`;
        const errorPart = this.errorType !== "None" ? " error" : "";
        const captions = this.captions;
        const actions = this.actions(captions, disabledPart)[this.mode];
        const showWaitingModeTexts = this.waitingMode === "editing" || this.waitingMode === "adding";
        return (h(Host, { role: "listitem", "aria-labelledby": NAME, class: {
                "gx-disabled": this.disabled
            } }, 
        // Add new field layout (last cell of the collection/entity)
        this.mode === "add" && this.waitingMode !== "adding" ? (this.newFieldMode(captions, errorPart, disabledPart, actions)) : this.level === 0 ? ( // Normal mode. Level === 0
        h("ch-accordion", { class: "accordion", part: `${PART_PREFIX}accordion`, accessibleName: this.name, expanded: this.expanded, exportparts: `accordion__chevron:${PART_PREFIX}chevron,accordion__expandable:${PART_PREFIX}expandable,accordion__header:${PART_PREFIX}header` }, this.normalMode(captions, errorPart, disabledPart, waitingModePart, actions, showWaitingModeTexts))) : (
        // Normal mode. Level !== 0
        this.normalMode(captions, errorPart, disabledPart, waitingModePart, actions, showWaitingModeTexts))));
    }
    static get is() { return "ch-next-data-modeling-item"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["next-data-modeling-item.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["next-data-modeling-item.css"]
        };
    }
    static get properties() {
        return {
            "actionsVisible": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the actions in the `mode === \"add\"` are\nvisible."
                },
                "attribute": "actions-visible",
                "reflect": false,
                "defaultValue": "true"
            },
            "captions": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "DataModelItemLabels",
                    "resolved": "{ collection: string; confirm: string; cancel: string; ATT: string; ENTITY: string; LEVEL: string; adding: string; addNewEntity: string; addNewField: string; edit: string; editing: string; delete: string; deleting: string; deleteMode: string; newEntity: string; newField: string; }",
                    "references": {
                        "DataModelItemLabels": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/next/data-modeling-item/next-data-modeling-item.tsx",
                            "id": "src/components/next/data-modeling-item/next-data-modeling-item.tsx::DataModelItemLabels"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The labels used in the buttons of the items. Important for accessibility."
                }
            },
            "dataType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The dataType of the field."
                },
                "attribute": "data-type",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "entityNameToATTs": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "EntityNameToATTs",
                    "resolved": "{ [key: string]: string[]; }",
                    "references": {
                        "EntityNameToATTs": {
                            "location": "import",
                            "path": "../data-modeling/data-model",
                            "id": "src/components/next/data-modeling/data-model.ts::EntityNameToATTs"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property maps entities of the current dataModel with their\ncorresponding ATTs."
                },
                "defaultValue": "{}"
            },
            "errorTexts": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "{ [key in ErrorText]: string }",
                    "resolved": "{ Empty: string; AlreadyDefined1: string; AlreadyDefinedEntity1: string; AlreadyDefined2: string; }",
                    "references": {
                        "ErrorText": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/next/data-modeling-item/next-data-modeling-item.tsx",
                            "id": "src/components/next/data-modeling-item/next-data-modeling-item.tsx::ErrorText"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The error texts used for the new field input."
                }
            },
            "fieldNames": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "string[]",
                    "resolved": "string[]",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property specifies the defined field names of the entity parent."
                },
                "defaultValue": "[]"
            },
            "level": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "0 | 1 | 2",
                    "resolved": "0 | 1 | 2",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property specifies at which collection level the field is located."
                },
                "attribute": "level",
                "reflect": false,
                "defaultValue": "1"
            },
            "maxAtts": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determine the maximum amount of ATTs displayed per entity."
                },
                "attribute": "max-atts",
                "reflect": false,
                "defaultValue": "3"
            },
            "mode": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "Mode",
                    "resolved": "\"add\" | \"delete\" | \"edit\" | \"normal\"",
                    "references": {
                        "Mode": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/next/data-modeling-item/next-data-modeling-item.tsx",
                            "id": "src/components/next/data-modeling-item/next-data-modeling-item.tsx::Mode"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute specifies the operating mode of the control"
                },
                "attribute": "mode",
                "reflect": false,
                "defaultValue": "\"normal\""
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The name of the field."
                },
                "attribute": "name",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "readonly": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute indicates that the user cannot modify the value of the control.\nSame as [readonly](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-readonly)\nattribute for `input` elements."
                },
                "attribute": "readonly",
                "reflect": false,
                "defaultValue": "false"
            },
            "showNewFieldBtn": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to show the new field button when `mode === \"add\"`"
                },
                "attribute": "show-new-field-btn",
                "reflect": false,
                "defaultValue": "true"
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "EntityItemType",
                    "resolved": "\"ATT\" | \"ENTITY\" | \"LEVEL\"",
                    "references": {
                        "EntityItemType": {
                            "location": "import",
                            "path": "../data-modeling/data-model",
                            "id": "src/components/next/data-modeling/data-model.ts::EntityItemType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The type of the field."
                },
                "attribute": "type",
                "reflect": false,
                "defaultValue": "\"LEVEL\""
            }
        };
    }
    static get states() {
        return {
            "expanded": {},
            "errorType": {},
            "waitingMode": {}
        };
    }
    static get events() {
        return [{
                "method": "deleteField",
                "name": "deleteField",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the item is confirmed to be deleted"
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "editField",
                "name": "editField",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the item is edited"
                },
                "complexType": {
                    "original": "ItemInfo",
                    "resolved": "{ name: string; type?: EntityItemType; }",
                    "references": {
                        "ItemInfo": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/next/data-modeling-item/next-data-modeling-item.tsx",
                            "id": "src/components/next/data-modeling-item/next-data-modeling-item.tsx::ItemInfo"
                        }
                    }
                }
            }, {
                "method": "newEntity",
                "name": "newEntity",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when a new entity is committed to be added"
                },
                "complexType": {
                    "original": "EntityInfo",
                    "resolved": "{ name: string; level: ItemInfo[]; }",
                    "references": {
                        "EntityInfo": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/next/data-modeling-item/next-data-modeling-item.tsx",
                            "id": "src/components/next/data-modeling-item/next-data-modeling-item.tsx::EntityInfo"
                        }
                    }
                }
            }, {
                "method": "firstNewField",
                "name": "firstNewField",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when a new file is committed to be added when adding a new entity (level === 0)"
                },
                "complexType": {
                    "original": "ItemInfo",
                    "resolved": "{ name: string; type?: EntityItemType; }",
                    "references": {
                        "ItemInfo": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/next/data-modeling-item/next-data-modeling-item.tsx",
                            "id": "src/components/next/data-modeling-item/next-data-modeling-item.tsx::ItemInfo"
                        }
                    }
                }
            }, {
                "method": "firstNewFieldErrors",
                "name": "firstNewFieldErrors",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the new field of the new entity tries to commits the adding\noperation, but fails because it has errors"
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "newField",
                "name": "newField",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when a new file is committed to be added"
                },
                "complexType": {
                    "original": "ItemInfo",
                    "resolved": "{ name: string; type?: EntityItemType; }",
                    "references": {
                        "ItemInfo": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/next/data-modeling-item/next-data-modeling-item.tsx",
                            "id": "src/components/next/data-modeling-item/next-data-modeling-item.tsx::ItemInfo"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "setAddingMode": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Set the adding mode for the first field of the entity.",
                    "tags": []
                }
            },
            "clearInput": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        },
                        "HTMLInputElement": {
                            "location": "global",
                            "id": "global::HTMLInputElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Remove the value of the input when mode === \"add\" | \"edit\"",
                    "tags": []
                }
            },
            "checkErrors": {
                "complexType": {
                    "signature": "(errors: \"yes\" | \"no\" | \"unknown\", event: CustomEvent | UIEvent) => Promise<void>",
                    "parameters": [{
                            "name": "errors",
                            "type": "\"no\" | \"yes\" | \"unknown\"",
                            "docs": ""
                        }, {
                            "name": "event",
                            "type": "UIEvent | CustomEvent<any>",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "CustomEvent": {
                            "location": "global",
                            "id": "global::CustomEvent"
                        },
                        "UIEvent": {
                            "location": "global",
                            "id": "global::UIEvent"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Check errors in the item when `level !== 0`",
                    "tags": []
                }
            },
            "delete": {
                "complexType": {
                    "signature": "(event: UIEvent) => Promise<void>",
                    "parameters": [{
                            "name": "event",
                            "type": "UIEvent",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "UIEvent": {
                            "location": "global",
                            "id": "global::UIEvent"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Deletes the field.",
                    "tags": []
                }
            },
            "hideWaitingMode": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLChNextDataModelingItemElement": {
                            "location": "global",
                            "id": "global::HTMLChNextDataModelingItemElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Hides the waiting mode to continue editing the field.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get listeners() {
        return [{
                "name": "expandedChange",
                "method": "handleExpandedChange",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "firstNewField",
                "method": "handleFirstNewField",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "firstNewFieldErrors",
                "method": "handleFirstNewFieldErrors",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=next-data-modeling-item.js.map
